import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldDefaultOptions, MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import { config } from 'src/assets/config';
import { AppRoutingModule } from '../app-routing.module';
import { AppComponent } from '../app.component';
import { QuillModule } from 'ngx-quill';
import { reducers } from '../store/reducers';
import { HighlightSearchPipe } from '../common/pipes/highlightable-search.pipe';
import { httpInterceptorProviders } from '../common/interceptors/interceptorProviders';
import { OverlayLoadingComponent } from '../controls/overlay-loading/overlay-loading.component';
import { CommonModule } from '@angular/common';
import { FilterBoxComponent } from '../controls/filter-box/filter-box.component';
import { CommentsBoxModule } from '../controls/comments-box/comments-box.module';
import { PartialOlogComponent } from '../components/olog/partial-olog/partial-olog.component';
import { NotificationComponent } from '../controls/notifications/notification.component';
import { AddRelatedLinkComponent } from '../controls/add-related-link/add-related-link.component';
import { LocationsService } from '../components/catalogs/beamline-catalog/resource/locations.service';
import { CancelDialogComponent } from '../controls/cancel-dialog/cancel-dialog.component';
import { WorkFlowService } from '../components/workflow/workflow.service';
import { TextareaDialogComponent } from '../controls/textarea-dialog/textarea-dialog.component';
import { CommentsAndAttachmentsModule } from '../controls/comments-and-attachments/comments-and-attachments.module';
import { RestrictionAvailableAutocompleteComponent } from '../controls/restrictions/restriction-available-autocomplete/restriction-available-autocomplete.component';
import { FormRestrictionTableComponent } from '../controls/restrictions/form-restriction-table/form-restriction-table.component';
import { ConfirmMessageComponent } from '../controls/confirm-message/confirm-message.component';
import { RelatedDocumentsComponent } from '../controls/related-documents/related-documents.component';
import { AcceleratorLocationChipsComponent } from '../controls/accelerator-location-chips/accelerator-location-chips.component';
import { RelatedLinkChipsComponent } from '../controls/related-link-chips/related-link-chips.component';
import { WorkerVerificationComponent } from '../controls/worker-verification/worker-verification.component';
import { ResourceAutocompleteComponent } from '../controls/resources/resource-autocomplete/resource-autocomplete.component';
import { FormTableComponent } from '../controls/form-table/form-table.component';
import { UserSelectComponent } from '../controls/user/user-select/user-select.component';
import { ResourceChipsComponent } from '../controls/resource-chips/resource-chips.component';
import { OtherSystemChipsComponent } from '../controls/other-system-chips/other-system-chips.component';
import { ExtraActionButtonComponent } from '../controls/checklist-components/extra-action-button/extra-action-button.component';
import { NormalInputDialogComponent } from '../controls/normal-input-dialog/normal-input-dialog.component';
import { DisapprovalReasonComponent } from '../controls/disapproval-reason/disapproval-reason.component';
import { APLCategoryAutocompleteComponent } from '../components/apl/apl-category-v2/controls/apl-category-autocomplete/apl-category-autocomplete.component';
import { APLGroupAutocompleteComponent } from '../components/apl/apl-category-v2/controls/apl-group-autocomplete/apl-group-autocomplete.component';
import { ProcedureAutocompleteComponent } from '../controls/procedure-autocomplete/procedure-autocomplete.component';
import { ActionItemEditComponent } from '../controls/catl/action-item/action-item-edit/action-item-edit.component';
import { ActionItemService } from '../controls/catl/action-item/action-item.service';
import { CommentsSummaryComponent } from '../controls/comments-summary/comments-summary.component';
import { UserAutocompletePopupComponent } from '../controls/user-autocomplete-popup/user-autocomplete-popup.component';
import { RejectDialogComponent } from '../controls/reject-dialog/reject-dialog.component';
import { ColorPickerComponent } from '../controls/color-picker/color-picker.component';
import { ChooseUsersComponent } from '../components/schedules/dialogs/choose-users/choose-users.component';
import { ChooseRolesComponent } from '../components/schedules/dialogs/choose-roles/choose-roles.component';
import { FileUploadV2Component } from '../controls/file-upload-v2/file-upload-v2.component';
import { RelatedLinkDocumentChipsComponent } from '../controls/related-link-document-chips/related-link-document-chips.component';
import { AddRelatedLinkDocumentComponent } from '../controls/add-related-link-document/add-related-link-document.component';
import { NotesComponent } from '../controls/notes/notes.component';
import { UserChipComponent } from '../components/apl/apl-category-v2/controls/user-chip/user-chip.component';
import { RoleChipComponent } from '../components/apl/apl-category-v2/controls/role-chip/role-chip.component';
import { RssTestShutterChipsComponent } from '../controls/rss-test-shutter-chips/rss-test-shutter-chips.component';
import { DatetimePickerComponent } from '../controls/datetime-picker/datetime-picker.component';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';

import { PvInfoComponent } from '../controls/pvinfo/pvinfo.component';
import { PvInfoDetailsComponent } from '../controls/pvinfo/pvinfo-details/pvinfo-details.component';
import { OperationalRestrictionsComponent } from '../components/catalogs/operational-restrictions/operational-restrictions.component';
import { CatalogHistoryNavigationComponent } from '../controls/catalog-history-navigation/catalog-history-navigation.component';
import { MatSliderModule } from '@angular/material/slider';
import { OlogButtonComponent } from '../components/olog/olog-button/olog-button.component';

import { TransactionLogComponent } from '../controls/transaction-log/transaction-log.component';
import { TransactionLogAmendmentsComponent } from '../controls/transaction-log/transaction-log-amendments/transaction-log-amendments.component';
import { TransactionLogApprovalsComponent } from '../controls/transaction-log/transaction-log-approvals/transaction-log-approvals.component';
import { EbMainS1Component } from '../components/eb/eb-main/eb-main-s1/eb-main-s1.component';
import { PpstbMainS1Component } from '../components/ppstb/ppstb-main/ppstb-main-s1/ppstb-main-s1.component';
import { WrapOnCommasPipe } from '../common/pipes/wrap-on-commas.pipe';
import { ControlsModule } from '../controls/controls.module';
import { ScfV2MainS1Component } from '../components/scf-v2/scf-v2-main/scf-v2-main-s1/scf-v2-main-s1.component';
import { ScfV2MainS2Component } from '../components/scf-v2/scf-v2-main/scf-v2-main-s2/scf-v2-main-s2.component';
import { ScfV2MainS4HoldpointsComponent } from '../components/scf-v2/scf-v2-main/scf-v2-main-s4/scf-v2-main-s4-holdpoints/scf-v2-main-s4-holdpoints.component';
import { HoldpointDialogComponent } from '../controls/holdpoint-dialog/holdpoint-dialog.component';
import { FormHistoryButtonComponent } from '../controls/form-history/form-history-button/form-history-button.component';
import { FormHistoryFormsComponent } from '../controls/form-history/form-history-forms/form-history-forms.component';
import { FormHistoryScfComponent } from '../controls/form-history/form-history-forms/form-history-scf/form-history-scf.component';
import { FormHistoryComponent } from '../controls/form-history/form-history.component';
import { FormHistoryRsswaComponent } from '../controls/form-history/form-history-forms/form-history-rsswa/form-history-rsswa.component';
import { RsswaSection1Component } from '../components/rsswa/rsswa-main-panel/rsswa-section1/rsswa-section1.component';
import { RsswaSection1FunctionalTestsComponent } from '../components/rsswa/rsswa-main-panel/rsswa-section1/rsswa-section1-functional-tests/rsswa-section1-functional-tests.component';
import { RssFunctionalTestListComponent } from '../components/rsswa/rsswa-main-panel/rss-functional-test-list/rss-functional-test-list.component';
import { RSSFunctionalTestPopupComponent } from '../components/rsswa/rsswa-main-panel/rss-functional-test-popup/rss-functional-test-popup.component';
import { RssFunctionalTestChipsComponent } from '../components/rsswa/rsswa-main-panel/rsswa-chips/rss-functional-test-chips/rss-functional-test-chips.component';
import { FormHistorySignaturesComponent } from '../controls/form-history/form-history-signatures/form-history-signatures.component';
import { LogSignatureComponent } from '../controls/form-history/form-history-signatures/log-signature/log-signature.component';
import { FormHistoryEbComponent } from '../controls/form-history/form-history-forms/form-history-eb/form-history-eb.component';
import { FormHistoryRsfComponent } from '../controls/form-history/form-history-forms/form-history-rsf/form-history-rsf.component';
import { RsfMainS1Component } from '../components/rsf/rsf-main/rsf-main-s1/rsf-main-s1.component';
import { RsfMainS1CheckcardComponent } from '../components/rsf/rsf-main/rsf-main-s1/rsf-main-s1-checkcard/rsf-main-s1-checkcard.component';
import { RsfMainS1FunctionalTestsComponent } from '../components/rsf/rsf-main/rsf-main-s1/rsf-main-s1-functional-tests/rsf-main-s1-functional-tests.component';
import { RsfMainS1NotificationsComponent } from '../components/rsf/rsf-main/rsf-main-s1/rsf-main-s1-notifications/rsf-main-s1-notifications.component';
import { RsfMainS1SystemsAffectedComponent } from '../components/rsf/rsf-main/rsf-main-s1/rsf-main-s1-systems-affected/rsf-main-s1-systems-affected.component';
import { RsfOlogDialogComponent } from '../components/rsf/rsf-main/dialogs/rsf-olog-dialog/rsf-olog-dialog.component';
import { StatusChipsComponent } from '../controls/status-chips/status-chips.component';
import { SharedChangesTooltipPipe } from '../common/pipes/shared-changes-tooltip/shared-changes-tooltip.pipe';
import { FileLinkPipeModule } from '../common/pipes/file-link/file-link-pipe.module';

const appearance: MatFormFieldDefaultOptions = {
  appearance: 'outline'
};

@NgModule({
  declarations: [
    RelatedLinkDocumentChipsComponent,
    AddRelatedLinkDocumentComponent,
    AddRelatedLinkComponent,
    UserAutocompletePopupComponent,
    UserChipComponent,
    RoleChipComponent,
    ColorPickerComponent,
    FileUploadV2Component,
    OverlayLoadingComponent,
    PartialOlogComponent,
    WorkerVerificationComponent,
    NotificationComponent,
    HighlightSearchPipe,
    FilterBoxComponent,
    CancelDialogComponent,
    TextareaDialogComponent,
    RestrictionAvailableAutocompleteComponent,
    FormRestrictionTableComponent,
    ConfirmMessageComponent,
    RelatedDocumentsComponent,
    RelatedLinkChipsComponent,
    AcceleratorLocationChipsComponent,
    ResourceAutocompleteComponent,
    FormTableComponent,
    UserSelectComponent,
    ResourceChipsComponent,
    OtherSystemChipsComponent,
    ExtraActionButtonComponent,
    NormalInputDialogComponent,
    DisapprovalReasonComponent,
    ActionItemEditComponent,
    RejectDialogComponent,
    APLCategoryAutocompleteComponent,
    APLGroupAutocompleteComponent,
    ProcedureAutocompleteComponent,
    CommentsSummaryComponent,
    ChooseUsersComponent,
    ChooseRolesComponent,
    NotesComponent,
    RssTestShutterChipsComponent,
    DatetimePickerComponent,
    PvInfoComponent,
    PvInfoDetailsComponent,
    OperationalRestrictionsComponent,
    CatalogHistoryNavigationComponent,
    OlogButtonComponent,
    TransactionLogComponent,
    TransactionLogApprovalsComponent,
    TransactionLogAmendmentsComponent,
    EbMainS1Component,
    PpstbMainS1Component,
    WrapOnCommasPipe,
    FormHistoryComponent,
    FormHistoryButtonComponent,
    FormHistoryScfComponent,
    FormHistoryFormsComponent,
    FormHistorySignaturesComponent,
    LogSignatureComponent,
    StatusChipsComponent,

    // Needed here to Form History
    ScfV2MainS1Component,
    ScfV2MainS2Component,
    ScfV2MainS4HoldpointsComponent,
    HoldpointDialogComponent,

    FormHistoryRsswaComponent,
    RsswaSection1Component,
    RsswaSection1FunctionalTestsComponent,
    RssFunctionalTestChipsComponent,
    RSSFunctionalTestPopupComponent,
    RssFunctionalTestListComponent,

    FormHistoryEbComponent,
    EbMainS1Component,

    FormHistoryRsfComponent,
    RsfMainS1Component,
    RsfMainS1CheckcardComponent,
    RsfMainS1NotificationsComponent,
    RsfMainS1SystemsAffectedComponent,
    RsfMainS1FunctionalTestsComponent,
    RsfOlogDialogComponent,
    LogSignatureComponent,
    StatusChipsComponent,
    SharedChangesTooltipPipe
  ],
  imports: [
    CommonModule,
    BrowserModule,
    FileLinkPipeModule,
    NgbModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatMenuModule,
    MatButtonModule,
    MatDialogModule,
    HttpClientModule,
    ScrollingModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatChipsModule,
    MatIconModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatRadioModule,
    MatStepperModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    MatGridListModule,
    MatProgressBarModule,
    DragDropModule,
    MatTreeModule,
    MatExpansionModule,
    MatSidenavModule,
    CommentsBoxModule,
    CommentsAndAttachmentsModule,
    ControlsModule,
    NgxMaterialTimepickerModule,
    FormsModule,
    MatSliderModule,
    QuillModule.forRoot(),
    NgxsModule.forRoot(reducers, {
      developmentMode: config.production
    }),
    NgxsReduxDevtoolsPluginModule.forRoot(),
  ],
  exports: [
    RouterModule,
    FileLinkPipeModule,
    RelatedLinkDocumentChipsComponent,
    AddRelatedLinkDocumentComponent,
    AddRelatedLinkComponent,
    UserChipComponent,
    RoleChipComponent,
    FileUploadV2Component,
    ColorPickerComponent,
    OverlayLoadingComponent,
    PartialOlogComponent,
    NotificationComponent,
    WorkerVerificationComponent,
    HighlightSearchPipe,
    FilterBoxComponent,
    CommentsBoxModule,
    CancelDialogComponent,
    TextareaDialogComponent,
    CommentsAndAttachmentsModule,
    RestrictionAvailableAutocompleteComponent,
    FormRestrictionTableComponent,
    ConfirmMessageComponent,
    RelatedDocumentsComponent,
    AcceleratorLocationChipsComponent,
    RelatedLinkChipsComponent,
    ResourceAutocompleteComponent,
    FormTableComponent,
    UserSelectComponent,
    ResourceChipsComponent,
    OtherSystemChipsComponent,
    ExtraActionButtonComponent,
    NormalInputDialogComponent,
    DisapprovalReasonComponent,
    ActionItemEditComponent,
    APLCategoryAutocompleteComponent,
    APLGroupAutocompleteComponent,
    ProcedureAutocompleteComponent,
    NotesComponent,
    RssTestShutterChipsComponent,
    DatetimePickerComponent,

    PvInfoComponent,
    PvInfoDetailsComponent,
    OperationalRestrictionsComponent,

    CatalogHistoryNavigationComponent,
    OlogButtonComponent,
    TransactionLogComponent,
    TransactionLogApprovalsComponent,
    TransactionLogAmendmentsComponent,
    EbMainS1Component,
    PpstbMainS1Component,
    ControlsModule,
    WrapOnCommasPipe,
    FormHistoryComponent,
    FormHistoryButtonComponent,
    FormHistorySignaturesComponent,
    LogSignatureComponent,
    StatusChipsComponent,

    ScfV2MainS1Component,
    ScfV2MainS2Component,
    ScfV2MainS4HoldpointsComponent,
    HoldpointDialogComponent,

    RsswaSection1Component,
    RsswaSection1FunctionalTestsComponent,
    RssFunctionalTestChipsComponent,
    RSSFunctionalTestPopupComponent,
    RssFunctionalTestListComponent,

    EbMainS1Component,
    RsfMainS1Component,
    RsfMainS1CheckcardComponent,
    RsfMainS1NotificationsComponent,
    RsfMainS1SystemsAffectedComponent,
    RsfMainS1FunctionalTestsComponent,
    LogSignatureComponent,
    StatusChipsComponent,
    SharedChangesTooltipPipe
  ],
  providers: [
    LocationsService,
    WorkFlowService,
    ActionItemService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: appearance
    },
    httpInterceptorProviders,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule { }
